import { Avatar, Button, Flex, Typography } from "antd";
import { AvailabilitySlots } from "components/AvailabilitySlots";
import { StyledNotification } from "components/AvailabilitySlots/styles";
import { Loader } from "components/Loader";
import { StartContent } from "components/StartContent";
import { SummaryTitle } from "components/StartContent/styled";
import { RESERVATION_OPTIONS, TRACKING } from "config/consts";
import { useApp } from "contexts/AppProvider";
import dayjs from "dayjs";
import { LOCALES } from "i18n/messages";
import {
  MAX_AVAILABILITY_MONTH_RETRIES,
  useGetAvailabilityQuery,
  useGetPersonalDataQuery,
} from "queries/reservation.query";
import { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useLocation, useNavigate } from "react-router-dom";
import { getAvailabilityParam } from "repositories/reservations/reservationRepository.param";
import { BranchType, ISingleAvailability, ISingleService } from "types";
import { uniqueOptionIds, withOption } from "lib/optionIds";
import { getDefaultVisitTypeOption } from "lib/reservationOptions";

type ISingleTherapist = { id: string; name: string };

export const Personal = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { locale } = useIntl();
  const { formatMessage } = useIntl();

  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const customerId = queryParams.get("id") ?? "";
  const userId = queryParams.get("uid") ?? queryParams.get("uId") ?? "";
  const requestedServiceId = queryParams.get("sId") ?? queryParams.get("sid");
  const requestedBranchId = queryParams.get("bId") ?? queryParams.get("bid");
  const isOnlineBranchRequested = requestedBranchId?.toLowerCase() === "online";
  const isBasic = queryParams.get("isBasic") === "true";
  const {
    setReservationPayload,
    dispatchReservationPayload,
    reservationPayload,
    customerId: contextCustomerId,
    activeTimeslot,
    setActiveTimeslot,
    price,
    getBranchById,
    activeService,
    setActiveService,
    setActiveStep,
    sentEvents,
    currency,
    setBranches: setContextBranches,
  } = useApp();

  const { data: personalData, isLoading: isLoadingPersonal } = useGetPersonalDataQuery(customerId, userId);
  const personalServices: ISingleService[] = useMemo(() => personalData?.services ?? [], [personalData]);
  const personalBranches: BranchType[] = useMemo(() => personalData?.branches ?? [], [personalData]);
  const personalTherapistIds = useMemo(
    () => Array.from(new Set(personalData?.therapists.map(({ id }) => id) ?? [])),
    [personalData],
  );
  const doesNotAcceptNewPatients = useMemo(() => {
    const therapistAssignments = personalData?.therapists ?? [];
    const selectedBranchIds = new Set(reservationPayload.branchIds);
    const relevantAssignments =
      selectedBranchIds.size > 0
        ? therapistAssignments.filter(({ branchId }) => selectedBranchIds.has(branchId))
        : therapistAssignments;

    return relevantAssignments.length > 0 && relevantAssignments.every(({ acceptsNewPatients }) => !acceptsNewPatients);
  }, [personalData, reservationPayload.branchIds]);

  const [branches, setBranches] = useState<BranchType[]>(personalBranches);
  const [services, setServices] = useState<ISingleService[]>(personalServices);
  const [isChild, setIsChild] = useState<boolean>(false);
  const [birthYear, setBirthYear] = useState<string | null>(reservationPayload.birthYear ?? null);
  const [activeCity, setActiveCity] = useState<string | null>(
    reservationPayload.isOnline ? (reservationPayload.branchIds.length > 0 ? "any" : "online") : null,
  );
  const [availabilityPayload, setAvailabilityPayload] = useState<getAvailabilityParam | null>(null);
  const [readyToFetch, setReadyToFetch] = useState<boolean>(false);
  const [defaultsApplied, setDefaultsApplied] = useState<boolean>(false);

  const [, setTimeSchedules] = useState<ISingleAvailability[]>([]);
  const [loadedDays, setLoadedDays] = useState<number>(30);
  const [availabilityAnchorDate, setAvailabilityAnchorDate] = useState<string | null>(null);
  const therapists: ISingleTherapist[] = [];
  const [accumulatedAvailability, setAccumulatedAvailability] = useState<ISingleAvailability[]>([]);
  const [retries, setRetries] = useState<number>(0);
  const personalCities = useMemo(() => Array.from(new Set(branches.map(({ city }) => city))), [branches]);

  const locationServices = useMemo(() => {
    if (!activeCity || activeCity === "any" || activeCity === "online") return services;

    const locationBranchIds = new Set(branches.filter(({ city }) => city === activeCity).map(({ id }) => id));
    const supportedServiceIds = new Set(
      (personalData?.therapists ?? [])
        .filter(({ branchId }) => locationBranchIds.has(branchId))
        .flatMap(({ services: therapistServices }) => therapistServices.map(({ serviceId }) => serviceId)),
    );

    return services.filter(({ id }) => supportedServiceIds.has(id));
  }, [activeCity, branches, personalData, services]);

  const {
    data: availability,
    isLoading,
    isFetching,
    isFetched,
    isPlaceholderData,
  } = useGetAvailabilityQuery(availabilityPayload, readyToFetch);

  // Redirect to / if uid is missing
  useEffect(() => {
    if (!userId) {
      navigate("/", { replace: true });
    }
  }, [userId, navigate]);

  // Sync local state with personal data
  useEffect(() => {
    if (personalBranches.length > 0) {
      setBranches(personalBranches);
      setContextBranches(personalBranches);
    }
  }, [personalBranches, setContextBranches]);

  useEffect(() => {
    if (personalServices.length > 0) setServices(personalServices);
  }, [personalServices]);

  // Apply defaults once data is loaded
  useEffect(() => {
    if (defaultsApplied) return;
    if (personalServices.length === 0 || personalBranches.length === 0) return;

    const persistedService = personalServices.find(({ id }) => id === reservationPayload.serviceId);
    const defaultService =
      persistedService ?? personalServices.find(({ id }) => id === requestedServiceId) ?? personalServices[0];
    const persistedVisitType = defaultService.options.find(
      (option) =>
        option.id === reservationPayload.visitTypeId &&
        option.category === RESERVATION_OPTIONS.VISIT_TYPE &&
        option.publicVisible !== false,
    );
    const visitTypeOption = persistedVisitType ?? getDefaultVisitTypeOption(defaultService);

    setActiveService(defaultService);
    if (!persistedService) {
      dispatchReservationPayload({
        type: "setService",
        serviceId: defaultService.id,
        prevActiveService: null,
        newActiveService: defaultService,
        locale,
      });
    }

    if (visitTypeOption && !persistedVisitType) {
      dispatchReservationPayload({
        type: "setVisitType",
        optionId: visitTypeOption.id,
        activeService: defaultService,
      });
    }

    const child = ["Dziecko", "Dziecko / nastolatek"].includes(visitTypeOption?.name ?? "");
    setIsChild(child);
    setBirthYear(child ? (reservationPayload.birthYear ?? null) : null);

    const onlineOptionId = defaultService.options.find(
      (el) => el.category === RESERVATION_OPTIONS.LOCATION && el.publicVisible !== false,
    )?.id;
    const requestedBranch = personalBranches.find(({ id }) => id === requestedBranchId);
    const persistedBranch = personalBranches.find(({ id }) => reservationPayload.branchIds.includes(id));
    const availableCities = Array.from(new Set(personalBranches.map(({ city }) => city)));

    if (isOnlineBranchRequested || (persistedService && reservationPayload.isOnline)) {
      if (reservationPayload.branchIds.length === 0) {
        dispatchReservationPayload({
          type: "setBranchOffline",
          onlineOptionId,
          branchIds: personalBranches.map((el) => el.id),
        });
      }
      dispatchReservationPayload({ type: "setIsOnline", isOnline: true, onlineOptionId });
      setActiveCity("any");
    } else if (persistedService && persistedBranch) {
      setActiveCity(persistedBranch.city);
    } else if (requestedBranch) {
      dispatchReservationPayload({
        type: "setBranchOffline",
        onlineOptionId,
        branchIds: [requestedBranch.id],
      });
      setActiveCity(requestedBranch.city);
    } else if (availableCities.length === 1) {
      const onlyCity = availableCities[0];
      dispatchReservationPayload({
        type: "setBranchOffline",
        onlineOptionId,
        branchIds: personalBranches.filter(({ city }) => city === onlyCity).map(({ id }) => id),
      });
      setActiveCity(onlyCity);
    } else {
      // Default to all physical locations. Online availability is opt-in.
      dispatchReservationPayload({
        type: "setBranchOffline",
        onlineOptionId,
        branchIds: personalBranches.map((el) => el.id),
      });
      setActiveCity("any");
    }

    setDefaultsApplied(true);
  }, [
    personalServices,
    personalBranches,
    requestedServiceId,
    requestedBranchId,
    isOnlineBranchRequested,
    defaultsApplied,
    locale,
    reservationPayload.serviceId,
    reservationPayload.visitTypeId,
    reservationPayload.birthYear,
    reservationPayload.isOnline,
    reservationPayload.branchIds,
    dispatchReservationPayload,
    setActiveService,
  ]);

  const handleBranchChange = (value: string | null) => {
    const onlineOptionId = activeService?.options.find(
      (el) => el.category === RESERVATION_OPTIONS.LOCATION && el.publicVisible !== false,
    )?.id;
    setActiveTimeslot(null);
    setTimeSchedules([]);
    setAccumulatedAvailability([]);
    setLoadedDays(30);
    setRetries(0);
    if (!value || value === "any") {
      dispatchReservationPayload({
        type: "setBranchOffline",
        onlineOptionId,
        branchIds: branches.map((el) => el.id),
      });
    } else {
      const b = branches.filter((el) => el.city === value);
      dispatchReservationPayload({
        type: "setBranchOffline",
        onlineOptionId,
        branchIds: b.map((el) => el.id),
      });
    }
    dispatchReservationPayload({
      type: "setIsOnline",
      isOnline: reservationPayload.isOnline,
      onlineOptionId,
    });

    setActiveCity(value);
    setAvailabilityAnchorDate(null);
  };

  const handleOnlineChange = (isOnline: boolean) => {
    const onlineOptionId = activeService?.options.find(
      (el) => el.category === RESERVATION_OPTIONS.LOCATION && el.publicVisible !== false,
    )?.id;
    setActiveTimeslot(null);
    setTimeSchedules([]);
    setAccumulatedAvailability([]);
    setLoadedDays(30);
    setRetries(0);
    dispatchReservationPayload({ type: "setIsOnline", isOnline, onlineOptionId });
    setAvailabilityAnchorDate(null);
  };

  const handleServiceChange = (value: string) => {
    const newService = services.find((el) => el.id === value);

    if (!newService) {
      console.error("Service not found");
      return;
    }

    const childOptionInNewService = activeService
      ? reservationPayload.optionIds
          .map((optionId) => activeService.options.find((el) => el.id === optionId)?.translations[locale])
          .filter(Boolean)
          .map((translation) => newService.options.find((el) => el.translations[locale] === translation))
          .find((option) => ["Dziecko", "Dziecko / nastolatek"].includes(option?.name ?? ""))
      : undefined;
    const child = !!childOptionInNewService;

    if (child !== isChild) {
      setIsChild(child);
    }

    dispatchReservationPayload({
      type: "setService",
      serviceId: value,
      prevActiveService: activeService,
      newActiveService: newService,
      locale,
    });

    setActiveService(newService);
    setActiveTimeslot(null);
    setTimeSchedules([]);
    setAccumulatedAvailability([]);
    setLoadedDays(30);
    setRetries(0);
    setAvailabilityAnchorDate(null);
  };

  useEffect(() => {
    if (!defaultsApplied || !activeService || locationServices.some(({ id }) => id === activeService.id)) return;

    if (locationServices.length > 0) {
      handleServiceChange(locationServices[0].id);
      return;
    }

    setActiveService(null);
    setReservationPayload((prev) => ({
      ...prev,
      serviceId: "",
      visitTypeId: "",
      optionIds: [],
      therapist: "",
    }));
    setActiveTimeslot(null);
  }, [activeCity, defaultsApplied, locationServices]);

  const handleOptionChange = (value: string) => {
    const optionName = activeService?.options.find((el) => el.id === value)?.name;
    const child = ["Dziecko", "Dziecko / nastolatek"].includes(optionName ?? "");

    setIsChild(child);

    if (!child) {
      setBirthYear(null);
    }

    dispatchReservationPayload({ type: "setVisitType", optionId: value, activeService });

    setReservationPayload((prev) => ({
      ...prev,
      therapist: "",
      birthYear: child ? prev.birthYear : null,
    }));
    setActiveTimeslot(null);
    setTimeSchedules([]);
    setAccumulatedAvailability([]);
    setLoadedDays(30);
    setRetries(0);
    setAvailabilityAnchorDate(null);
  };

  const handleNextClick = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    const nextParams = new URLSearchParams(location.search);
    nextParams.set("personal", "1");
    navigate(`/form?${nextParams.toString()}`);
  };

  const handleBirthYear = (value: string) => {
    setBirthYear(value);
    setReservationPayload({
      ...reservationPayload,
      birthYear: value,
    });
    setAccumulatedAvailability([]);
  };

  const handleLoadMore = () => {
    const newDays = loadedDays + 30;
    setLoadedDays(newDays);
    setAvailabilityPayload((prev) =>
      prev ? { ...prev, dateTo: dayjs().add(newDays, "days").format("YYYY-MM-DD") } : prev,
    );
  };

  useEffect(() => {
    if (
      !readyToFetch ||
      !availability ||
      availability.length > 0 ||
      isFetching ||
      isPlaceholderData ||
      !availabilityPayload
    ) {
      return;
    }

    if (!availabilityPayload.includeOnline) {
      const onlineOptionId = activeService?.options.find(
        (el) => el.category === RESERVATION_OPTIONS.LOCATION && el.publicVisible !== false,
      )?.id;

      if (!onlineOptionId) return;

      setLoadedDays(30);
      setRetries(0);
      setAccumulatedAvailability([]);
      setTimeSchedules([]);
      setActiveTimeslot(null);
      setAvailabilityAnchorDate(null);
      setAvailabilityPayload((prev) =>
        prev
          ? {
              ...prev,
              includeOnline: true,
              optionIds: uniqueOptionIds(withOption(prev.optionIds ?? [], onlineOptionId)),
            }
          : prev,
      );
      return;
    }

    if (retries < MAX_AVAILABILITY_MONTH_RETRIES) {
      setRetries((prev) => prev + 1);
      handleLoadMore();
    }
  }, [
    availability,
    availabilityPayload,
    isFetching,
    isPlaceholderData,
    retries,
    activeService,
    setActiveTimeslot,
    readyToFetch,
  ]);

  useEffect(() => {
    if (readyToFetch && !isPlaceholderData && availability && availability.length > 0) {
      setAccumulatedAvailability((prev) => {
        const prevIds = new Set(prev.map((el) => `${el.scheduleId}_${el.date}_${el.startTime}`));
        const newItems = availability.filter((el) => !prevIds.has(`${el.scheduleId}_${el.date}_${el.startTime}`));
        return newItems.length > 0 ? [...prev, ...newItems] : prev;
      });
    }
  }, [availability, isPlaceholderData, readyToFetch]);

  useEffect(() => {
    if (
      defaultsApplied &&
      (reservationPayload.serviceId || reservationPayload.isOnline || reservationPayload.branchIds.length > 0) &&
      !sentEvents.includes(TRACKING.OPEN)
    ) {
      setActiveStep(TRACKING.OPEN);
    }
  }, [
    defaultsApplied,
    reservationPayload.serviceId,
    reservationPayload.isOnline,
    reservationPayload.branchIds,
    sentEvents,
    setActiveStep,
  ]);

  useEffect(() => {
    if (isFetched) {
      setActiveStep(TRACKING.SEARCH);
    }
  }, [isFetched, setActiveStep]);

  useEffect(() => {
    if (activeService && locale === LOCALES.ENGLISH) {
      const lang = activeService.options.find(
        (el) => el.category === RESERVATION_OPTIONS.LANGUGAGE && el.publicVisible !== false,
      );
      if (lang) {
        dispatchReservationPayload({ type: "setLanguageOption", optionId: lang.id, enabled: true });
      }
    }
  }, [activeService, locale, dispatchReservationPayload]);

  // Limit availability to every branch-specific therapist assignment for this user.
  useEffect(() => {
    if (doesNotAcceptNewPatients) {
      setReadyToFetch(false);
      setAvailabilityPayload(null);
      return;
    }

    const hasRequiredSelection =
      defaultsApplied &&
      reservationPayload.visitTypeId &&
      reservationPayload.serviceId &&
      personalTherapistIds.length > 0 &&
      (activeCity || reservationPayload.isOnline);

    if (!hasRequiredSelection || (isChild && !birthYear)) {
      setReadyToFetch(false);
      return;
    }

    const onlineOptionId = activeService.options.find(
      (el) => el.category === RESERVATION_OPTIONS.LOCATION && el.publicVisible !== false,
    )?.id;
    const optionIds = uniqueOptionIds(reservationPayload.optionIds);
    const payload: getAvailabilityParam = {
      customerId: customerId || contextCustomerId,
      serviceIds: [reservationPayload.serviceId],
      branchIds: reservationPayload.branchIds,
      optionIds: reservationPayload.isOnline ? optionIds : optionIds.filter((optionId) => optionId !== onlineOptionId),
      childBirthYear: isChild ? birthYear : null,
      dateFrom: dayjs().format("YYYY-MM-DD"),
      dateTo: dayjs().add(30, "days").format("YYYY-MM-DD"),
      includeOnline: reservationPayload.isOnline,
      therapistIds: personalTherapistIds,
    };

    setAvailabilityPayload(payload);
    setReadyToFetch(true);
  }, [
    defaultsApplied,
    doesNotAcceptNewPatients,
    reservationPayload,
    isChild,
    birthYear,
    activeCity,
    customerId,
    contextCustomerId,
    personalTherapistIds,
  ]);

  useEffect(() => {
    setAvailabilityAnchorDate(null);
  }, [reservationPayload.therapist]);

  useEffect(() => {
    setAccumulatedAvailability([]);
    setTimeSchedules([]);
    setActiveTimeslot(null);
    setAvailabilityAnchorDate(null);
    setReservationPayload((prev) => ({ ...prev, therapist: "" }));
  }, [reservationPayload.optionIds]);

  const branch = activeTimeslot ? getBranchById(activeTimeslot.branchId) : null;
  const childAge = 2024 - parseInt(birthYear || "2024");

  if (!userId) return null;

  if (isLoadingPersonal || !personalData) {
    return <Loader />;
  }

  if (personalServices.length === 0 || personalBranches.length === 0) {
    return (
      <StyledNotification vertical align="center" style={{ marginTop: 20 }}>
        <Typography.Text>{formatMessage({ defaultMessage: "Brak dostępnych terminów", id: "EDBKe2" })}</Typography.Text>
      </StyledNotification>
    );
  }

  return (
    <div className="personal-booking">
      {services?.length && branches?.length ? (
        <div className="personal-booking__grid">
          <div className="personal-booking__details">
            {!isBasic && (
              <Flex
                gap={16}
                align="flex-start"
                style={{
                  padding: 20,
                  marginBottom: 16,
                  border: "1px solid #e8e8e8",
                  borderRadius: 14,
                  background: "#fff",
                }}
              >
                <Avatar src={personalData.specialist.avatarUrl} size={88} style={{ flex: "0 0 auto" }} />
                <Flex vertical gap={3}>
                  <Typography.Title level={4} style={{ margin: 0 }}>
                    {personalData.specialist.firstName} {personalData.specialist.lastName}
                  </Typography.Title>
                  <Typography.Text type="secondary">{personalData.specialist.title}</Typography.Text>
                  <Typography.Paragraph style={{ margin: "8px 0 0", lineHeight: 1.55 }}>
                    {personalData.specialist.bio}
                  </Typography.Paragraph>
                </Flex>
              </Flex>
            )}
            <StartContent
              activeCity={activeCity}
              cities={personalCities}
              handleBranchChange={handleBranchChange}
              handleOnlineChange={handleOnlineChange}
              handleServiceChange={handleServiceChange}
              handleOptionChange={handleOptionChange}
              activeService={activeService}
              services={locationServices}
              isChild={isChild}
              birthYear={birthYear}
              setBirthYear={handleBirthYear}
              availability={readyToFetch ? availability : undefined}
              therapists={therapists}
              personalMode
              locationSelectionDisabled={isOnlineBranchRequested}
            />
          </div>
          <div className="personal-booking__availability">
            {doesNotAcceptNewPatients ? (
              <StyledNotification vertical align="center" style={{ marginTop: 20, padding: 10 }}>
                <Typography.Text>
                  {formatMessage({
                    defaultMessage: "Brak wolnych terminów. Obecnie specjalista nie przyjmuje nowych pacjentów",
                    id: "TwJC9j",
                  })}
                </Typography.Text>
              </StyledNotification>
            ) : readyToFetch &&
              isFetched &&
              ((availability && availability.length > 0) || retries === MAX_AVAILABILITY_MONTH_RETRIES) &&
              reservationPayload.visitTypeId ? (
              <>
                {accumulatedAvailability && accumulatedAvailability.length > 0 ? (
                  <div style={{ padding: 20 }}>
                    <SummaryTitle>{formatMessage({ defaultMessage: "Dostępne terminy", id: "hpEV8w" })}</SummaryTitle>
                    <AvailabilitySlots
                      duration={activeService.bookingTime}
                      availability={accumulatedAvailability}
                      loadedDays={loadedDays}
                      setTimeSchedules={setTimeSchedules}
                      onLoadMore={handleLoadMore}
                      isLoadingMore={isFetching}
                      persistedAnchorDate={availabilityAnchorDate}
                      onAnchorDateChange={setAvailabilityAnchorDate}
                    />
                    {activeTimeslot && (
                      <div style={{ marginTop: 20 }}>
                        <div>
                          <Typography.Text style={{ margin: "10px 10px 0 0", display: "inline-block", minWidth: 110 }}>
                            {formatMessage({ defaultMessage: "Data", id: "XHHR08" })}:{" "}
                          </Typography.Text>
                          <Typography.Text strong>
                            {dayjs(`${activeTimeslot.date} ${activeTimeslot.startTime}`, "YYYY-MM-DD HH:mm:ss").format(
                              "DD MMMM YYYY, HH:mm",
                            )}
                          </Typography.Text>
                        </div>
                        <div>
                          <Typography.Text style={{ marginRight: "10px", display: "inline-block", minWidth: 110 }}>
                            {formatMessage({ defaultMessage: "Koszt wizyty", id: "qfbfoZ" })}:
                          </Typography.Text>

                          <Typography.Text strong>
                            {price}
                            {currency}
                          </Typography.Text>
                        </div>
                        <div>
                          <Typography.Text style={{ marginRight: 10, display: "inline-block", minWidth: 110 }}>
                            {formatMessage({ defaultMessage: "Adres gabinetu", id: "IvLSQl" })}:
                          </Typography.Text>
                          <Typography.Text strong>
                            {reservationPayload.isOnline || activeTimeslot.isOnline
                              ? formatMessage({ defaultMessage: "Wizyta online", id: "iysARY" })
                              : branch
                                ? `${branch.street}, ${branch.city}`
                                : ""}
                          </Typography.Text>
                        </div>
                        {isChild && childAge <= activeTimeslot.therapist.noChildOnFirstVisitAge && (
                          <div style={{ marginTop: 20, fontStyle: "italic" }}>
                            <Typography.Text>
                              {formatMessage({
                                defaultMessage:
                                  "Uwaga: Na pierwszą wizytę zapraszamy jednego lub oboje rodziców/opiekunów",
                                id: "TJusWe",
                              })}
                            </Typography.Text>{" "}
                            <Typography.Text strong>
                              {formatMessage({
                                defaultMessage: "BEZ dziecka",
                                id: "Pj/7gf",
                              })}
                              .
                            </Typography.Text>
                          </div>
                        )}

                        <Flex justify="flex-end" style={{ width: "100%", marginTop: 20 }}>
                          <Button type="primary" onClick={handleNextClick} style={{ boxShadow: "none" }}>
                            {formatMessage({ defaultMessage: "Dalej", id: "+N4HKT" })}
                          </Button>
                        </Flex>
                      </div>
                    )}
                  </div>
                ) : (
                  <StyledNotification vertical align="center" style={{ marginTop: 20 }}>
                    <Typography.Text>
                      {formatMessage({ defaultMessage: "Brak dostępnych terminów", id: "EDBKe2" })}
                    </Typography.Text>
                  </StyledNotification>
                )}
              </>
            ) : (
              <>
                {readyToFetch &&
                  (isLoading ||
                    isPlaceholderData ||
                    (availability &&
                      availability.length === 0 &&
                      (retries > 0 || !availabilityPayload?.includeOnline) &&
                      retries < MAX_AVAILABILITY_MONTH_RETRIES)) && <Loader />}
              </>
            )}
          </div>
        </div>
      ) : (
        <Loader />
      )}
    </div>
  );
};
